// Всплывающие меню и списки (README §12: «все плавающие меню и списки
// ограничены краями экрана»). Рисуются порталом в <body> (токены там есть:
// .abx стоит на <html>), позиция fixed от якоря, после замера — сдвиг внутрь
// экрана с полем 8 px. Закрываются кликом мимо и Esc.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import './ui.css'

export type Placement =
  | 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'right-start' | 'left-start'

/** Якорь: элемент, прямоугольник или точка (контекстное меню). */
export type Anchor = HTMLElement | DOMRect | { x: number; y: number } | null

export interface PopoverProps {
  open: boolean
  anchor: Anchor
  onClose: () => void
  placement?: Placement
  /** зазор от якоря, px */
  offset?: number
  /** ширина, px; иначе по содержимому */
  width?: number
  className?: string
  style?: CSSProperties
  children: ReactNode
  /** не закрывать по клику мимо (например, пока идёт перетаскивание) */
  modal?: boolean
}

const MARGIN = 8

function rectOf(anchor: Anchor): DOMRect | null {
  if (!anchor) return null
  if (anchor instanceof HTMLElement) return anchor.getBoundingClientRect()
  if (anchor instanceof DOMRect) return anchor
  return new DOMRect(anchor.x, anchor.y, 0, 0)
}

/** Поставить коробку w×h у прямоугольника r и зажать её в окне. */
export function placeBox(
  r: DOMRect, w: number, h: number, placement: Placement, offset: number,
  vw = window.innerWidth, vh = window.innerHeight,
): { left: number; top: number } {
  let left = r.left
  let top = r.bottom + offset
  switch (placement) {
    case 'bottom-end': left = r.right - w; break
    case 'top-start': top = r.top - offset - h; break
    case 'top-end': top = r.top - offset - h; left = r.right - w; break
    case 'right-start': left = r.right + offset; top = r.top; break
    case 'left-start': left = r.left - offset - w; top = r.top; break
    default: break
  }
  // не влезло снизу — переворачиваем наверх, и наоборот
  if (placement.startsWith('bottom') && top + h > vh - MARGIN && r.top - offset - h >= MARGIN) {
    top = r.top - offset - h
  } else if (placement.startsWith('top') && top < MARGIN && r.bottom + offset + h <= vh - MARGIN) {
    top = r.bottom + offset
  }
  if (placement === 'right-start' && left + w > vw - MARGIN) left = r.left - offset - w
  if (placement === 'left-start' && left < MARGIN) left = r.right + offset
  left = Math.min(Math.max(MARGIN, left), Math.max(MARGIN, vw - MARGIN - w))
  top = Math.min(Math.max(MARGIN, top), Math.max(MARGIN, vh - MARGIN - h))
  return { left, top }
}

export function Popover({
  open, anchor, onClose, placement = 'bottom-start', offset = 6, width, className, style, children, modal,
}: PopoverProps) {
  const boxRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)

  const measure = useCallback(() => {
    const box = boxRef.current
    const r = rectOf(anchor)
    if (!box || !r) return
    setPos(placeBox(r, box.offsetWidth, box.offsetHeight, placement, offset))
  }, [anchor, placement, offset])

  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    measure()
  }, [open, measure])

  useEffect(() => {
    if (!open) return
    const box = boxRef.current
    const ro = box ? new ResizeObserver(measure) : null
    if (box && ro) ro.observe(box)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    const onDown = (event: PointerEvent) => {
      if (modal) return
      const target = event.target as Node
      if (boxRef.current?.contains(target)) return
      if (anchor instanceof HTMLElement && anchor.contains(target)) return
      onClose()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('keydown', onKey, true)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open, anchor, onClose, measure, modal])

  if (!open) return null
  return createPortal(
    <div
      ref={boxRef}
      className={`abx-pop${className ? ' ' + className : ''}`}
      style={{
        width,
        maxWidth: `calc(100vw - ${MARGIN * 2}px)`,
        left: pos?.left ?? -9999,
        top: pos?.top ?? -9999,
        visibility: pos ? 'visible' : 'hidden',
        ...style,
      }}
    >
      {children}
    </div>,
    document.body,
  )
}

// ---------- меню ----------

export interface MenuItem {
  id: string
  label?: ReactNode
  /** справа мелко: клавиша, счётчик */
  hint?: ReactNode
  /** слева: значок */
  icon?: ReactNode
  active?: boolean
  danger?: boolean
  disabled?: boolean
  /** линия-разделитель вместо пункта */
  separator?: boolean
  onSelect?: () => void
}

export interface MenuProps extends Omit<PopoverProps, 'children'> {
  items: MenuItem[]
  /** над пунктами (поиск, заголовок) */
  header?: ReactNode
  /** под пунктами («открыть библиотеку →») */
  footer?: ReactNode
  /** не закрывать после выбора */
  keepOpen?: boolean
}

export function Menu({ items, header, footer, keepOpen, ...pop }: MenuProps) {
  const [cursor, setCursor] = useState(-1)
  const listRef = useRef<HTMLDivElement>(null)
  const selectable = items.filter((item) => !item.separator && !item.disabled)

  useEffect(() => {
    if (!pop.open) {
      setCursor(-1)
      return
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        const step = event.key === 'ArrowDown' ? 1 : -1
        setCursor((c) => (c + step + selectable.length) % Math.max(1, selectable.length))
      } else if (event.key === 'Enter' && cursor >= 0 && selectable[cursor]) {
        event.preventDefault()
        selectable[cursor].onSelect?.()
        if (!keepOpen) pop.onClose()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [pop.open, cursor, selectable, keepOpen, pop])

  return (
    <Popover {...pop} className={`abx-menu${pop.className ? ' ' + pop.className : ''}`}>
      {header}
      <div ref={listRef} role="menu">
        {items.map((item) => {
          if (item.separator) return <div key={item.id} className="abx-menu__sep" role="separator" />
          const focused = selectable[cursor]?.id === item.id
          return (
            <button
              key={item.id}
              role="menuitem"
              disabled={item.disabled}
              className={[
                'abx-menu__item',
                item.active ? 'is-active' : '',
                item.danger ? 'is-danger' : '',
                focused ? 'is-focus' : '',
              ].filter(Boolean).join(' ')}
              onMouseEnter={() => setCursor(selectable.findIndex((x) => x.id === item.id))}
              onClick={() => {
                item.onSelect?.()
                if (!keepOpen) pop.onClose()
              }}
            >
              {item.icon ? <span className="abx-menu__icon">{item.icon}</span> : null}
              <span className="abx-menu__label">{item.label}</span>
              {item.hint !== undefined ? <span className="abx-menu__hint">{item.hint}</span> : null}
            </button>
          )
        })}
      </div>
      {footer}
    </Popover>
  )
}
